import React, {
  useEffect, useCallback, useState, memo,
} from 'react'
import {
  func, string, bool,
} from 'prop-types'
import noop from 'lodash/noop'
import {
  MFAStepOne,
  MFAStepTwo,
  VerifyPhoneStepOne,
  VerifyPhoneStepTwo,
  mfaConfig,
} from '../../common/MFAByStep'
import { calculateCounter } from '../../common/MFAContent/MFAContent'
import useAutoClosedAlert from '../../../shared/customHooks/useAutoClosedAlert'

const MODES = mfaConfig.StepModeMap

function MFAV2ByStep({
  messageType,
  messageContent,
  senderEmail,
  senderPhone,
  isTrustDevice,
  nextTimeWindow,
  locale,
  handleOperation,
  stepMode,
  defaultWay,
  channel,
  isShowBackBtn,
}) {
  const [countDownNumber, setCountDownNumber] = useState(calculateCounter(nextTimeWindow))

  useAutoClosedAlert({ messageType, messageContent })

  useEffect(() => {
    setCountDownNumber(calculateCounter(nextTimeWindow))
    const interval = setInterval(() => {
      const count = calculateCounter(nextTimeWindow)
      setCountDownNumber(count)
      if (count <= 0) clearInterval(interval)
    }, 1000)
    return () => {
      clearInterval(interval)
    }
  }, [nextTimeWindow])

  const handleClose = useCallback(() => {
    handleOperation({
      operation: mfaConfig.Operations.CLOSE,
    })
  }, [handleOperation])

  const handleReject = useCallback(() => {
    handleOperation({
      operation: mfaConfig.Operations.REJECT,
    })
  }, [handleOperation])

  const handleVerify = useCallback(values => {
    handleOperation({
      operation: mfaConfig.Operations.VERIFY,
      ...values,
    })
  }, [handleOperation])

  const handleResend = useCallback(() => {
    handleOperation({
      operation: mfaConfig.Operations.RESENDCODE,
    })
  }, [handleOperation])

  const handleConfirm = useCallback(values => {
    handleOperation({
      operation: mfaConfig.Operations.CONFIRM,
      ...values,
    })
  }, [handleOperation])

  const handleToVerifyOne = useCallback(() => {
    handleOperation({
      operation: mfaConfig.Operations.TO_VERIFY_ONE,
    })
  }, [handleOperation])

  const handleToVerifyTwo = useCallback(values => {
    handleOperation({
      operation: mfaConfig.Operations.TO_VERIFY_TWO,
      ...values,
    })
  }, [handleOperation])

  const handleConfirmStepOne = useCallback(values => {
    handleConfirm(values)
  }, [handleConfirm])

  return (
    <>
      {
        (stepMode === MODES.MFA_STEP_ONE_FOR_AUI || stepMode === MODES.MFA_STEP_ONE_FOR_CUI) && (
          <MFAStepOne
            emailAddress={senderEmail}
            phoneNumber={senderPhone}
            defaultWay={defaultWay}
            handleClose={handleClose}
            handleReject={handleClose}
            handleConfirm={handleConfirmStepOne}
          />
        )
      }
      {
        (stepMode === MODES.MFA_STEP_TWO_FOR_AUI || stepMode === MODES.MFA_STEP_TWO_FOR_CUI) && (
          <MFAStepTwo
            emailAddress={senderEmail}
            phoneNumber={senderPhone}
            countDownNumber={countDownNumber}
            channel={channel}
            isTrustDevice={isTrustDevice}
            isShowBackBtn={isShowBackBtn}
            handleClose={handleClose}
            handleConfirm={handleVerify}
            handleReject={handleReject}
            handleResend={handleResend}
          />
        )
      }
      {stepMode === MODES.VERIFY_PHONE_STEP_ONE && (
        <VerifyPhoneStepOne
          phoneNumber={senderPhone}
          defaultLocale={locale}
          title="passport.verification.modal.phone.verify"
          rejectButtonText="passport.lib.components.common.btn.tryAnother"
          handleClose={handleClose}
          handleReject={handleReject}
          handleConfirm={handleToVerifyTwo}
        />
      )}
      {stepMode === MODES.VERIFY_PHONE_STEP_TWO && (
        <VerifyPhoneStepTwo
          handleClose={handleClose}
          handleConfirm={handleVerify}
          handleResend={handleResend}
          handleReject={handleToVerifyOne}
          phoneNumber={senderPhone}
          title="passport.verification.modal.phone.verify"
          okText="passport.common.btn.verify"
          countDownNumber={countDownNumber}
        />
      )}
    </>
  )
}

MFAV2ByStep.propTypes = {
  messageType: string,
  messageContent: string,
  senderEmail: string,
  senderPhone: string,
  isTrustDevice: bool,
  nextTimeWindow: string,
  locale: string,
  handleOperation: func,
  stepMode: string,
  defaultWay: string,
  channel: string,
  isShowBackBtn: bool,
}

MFAV2ByStep.defaultProps = {
  messageType: '',
  messageContent: '',
  senderEmail: '',
  senderPhone: '',
  isTrustDevice: true,
  nextTimeWindow: '',
  locale: 'en_US',
  handleOperation: noop,
  stepMode: '',
  defaultWay: '',
  channel: '',
  isShowBackBtn: true,
}

export default memo(MFAV2ByStep)
